import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, type Header } from "@tanstack/react-table";
import { formatTableColumnLabel } from "../DataTable/formatTableColumnLabel";
import type { DataTableColumnMeta } from "../DataTable/DataTable";
import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical } from "lucide-react";
import { type CSSProperties, type ReactNode } from "react";

import { cn } from "@/lib/utils/cn";
import { TableHeaderContextMenu } from "../DataTable/TableHeaderContextMenu";

function DraggableCardTableHeaderLabelAndSort({
  header,
  labelEl,
  fixedColumnWidths,
}: {
  header: Header<unknown, unknown>;
  labelEl: ReactNode;
  fixedColumnWidths: boolean;
}) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const columnLabel = formatTableColumnLabel(
    header.column.id,
    (header.column.columnDef.meta as DataTableColumnMeta | undefined)
      ?.preserveHeaderLabel,
  );

  if (!canSort) {
    return (
      <span
        className={cn(
          "min-w-0 gap-1.5",
          fixedColumnWidths
            ? "flex items-start"
            : "inline-flex h-4 items-center whitespace-nowrap",
        )}
      >
        {labelEl}
      </span>
    );
  }

  const Icon =
    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;

  return (
    <button
      type="button"
      className={cn(
        "text-foreground hover:bg-muted/50 max-w-full min-w-0 cursor-pointer gap-1.5 rounded-sm p-0 text-left text-xs leading-4 font-medium",
        fixedColumnWidths
          ? "flex items-start"
          : "inline-flex h-4 items-center whitespace-nowrap",
      )}
      aria-label={`Sort by ${columnLabel}`}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={header.column.getToggleSortingHandler()}
    >
      <span
        className={cn(
          "min-w-0",
          fixedColumnWidths && "break-words whitespace-normal",
        )}
      >
        {labelEl}
      </span>
      <Icon
        className={cn(
          "text-muted-foreground h-3.5 w-3.5 shrink-0",
          sorted ? "text-foreground" : "opacity-55",
        )}
        aria-hidden
      />
    </button>
  );
}

/**
 * Draggable column header: grip handle + inline sort (My Matters / card table style).
 * Use with {@link DraggableColumnTableCell} so the whole column moves during drag.
 */
export function DraggableCardTableColumnHeader<T>({
  header,
  fixedColumnWidths = false,
  sticky = false,
}: {
  header: Header<T, unknown>;
  fixedColumnWidths?: boolean;
  sticky?: boolean;
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  const style: CSSProperties = {
    opacity: isDragging ? 0.65 : 1,
    position: sticky ? "sticky" : "relative",
    top: sticky ? 0 : undefined,
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? "none" : "transform 200ms ease-in-out",
    minWidth: header.column.getSize(),
    ...(fixedColumnWidths
      ? {
          width: header.column.getSize(),
          maxWidth: header.column.getSize(),
        }
      : {}),
    zIndex: isDragging ? 30 : sticky ? 20 : 0,
  };

  const labelEl = flexRender(
    header.column.columnDef.header,
    header.getContext(),
  );
  const columnLabel = formatTableColumnLabel(
    header.column.id,
    (header.column.columnDef.meta as DataTableColumnMeta | undefined)
      ?.preserveHeaderLabel,
  );

  return (
    <TableHeaderContextMenu
      header={header}
      trigger={
        <th
          ref={setNodeRef}
          style={style}
          className={cn(
            "text-foreground px-4 py-3 text-left align-middle text-xs leading-4 font-medium",
            fixedColumnWidths && "overflow-hidden",
            sticky && "bg-muted",
          )}
        >
          {header.isPlaceholder ? null : (
            <span
              className={cn(
                "max-w-full cursor-grab gap-1.5 select-none active:cursor-grabbing",
                fixedColumnWidths
                  ? "flex items-start"
                  : "inline-flex h-4 items-center",
              )}
            >
              <button
                type="button"
                aria-label={`Reorder ${columnLabel} column`}
                className="text-muted-foreground inline-flex cursor-grab items-center rounded-sm active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </button>
              <DraggableCardTableHeaderLabelAndSort
                header={header as Header<unknown, unknown>}
                labelEl={labelEl}
                fixedColumnWidths={fixedColumnWidths}
              />
            </span>
          )}
        </th>
      }
    />
  );
}
